import { Controller } from "@hotwired/stimulus"
import I18n from "../../i18n"
import { formatCents } from "./currency.mjs"

const t = (key, options) => I18n.t(`erp.sales.js.autosave.${key}`, options)

export default class extends Controller {
  static targets = ["status", "closeButton"]
  static values = { delay: { type: Number, default: 1000 } }

  connect() {
    this._timeout = null
    this._saving = false
    this._dirty = false
    this._submitQueued = false
    this._conflicted = false
    this._editGeneration = 0

    this._onSubmit = this.#handleSubmit.bind(this)
    this.element.addEventListener("submit", this._onSubmit)
    this._onInvalid = this.#handleInvalid.bind(this)
    this.element.addEventListener("invalid", this._onInvalid, true)

    this._onBeforeUnload = this.#handleBeforeUnload.bind(this)
    window.addEventListener("beforeunload", this._onBeforeUnload)
    this._onTurboBeforeVisit = this.#handleTurboBeforeVisit.bind(this)
    document.addEventListener("turbo:before-visit", this._onTurboBeforeVisit)
    this._updateStatus("saved")
  }

  disconnect() {
    clearTimeout(this._timeout)
    this.element.removeEventListener("submit", this._onSubmit)
    this.element.removeEventListener("invalid", this._onInvalid, true)
    window.removeEventListener("beforeunload", this._onBeforeUnload)
    document.removeEventListener("turbo:before-visit", this._onTurboBeforeVisit)
  }

  save(event) {
    // For native input/change events, only trigger for actual form fields
    if ((event.type === "input" || event.type === "change") && !event.target.name) return
    if (event.target.form && event.target.form !== this.element) return

    if (event.target instanceof HTMLInputElement && event.target.type === "file") {
      if (event.target.files.length === 0) return

      const error = this._selectedFileError(event.target)
      if (error) {
        event.target.value = ""
        this._updateStatus("error", null, [error])
        if (this.hasStatusTarget) this.statusTarget.focus()
        return
      }
    }

    this._dirty = true
    if (this._conflicted) return
    this._editGeneration += 1
    clearTimeout(this._timeout)
    this._updateStatus("modified")
  }

  saveNow() {
    this._dirty = true
    if (this._conflicted) return
    this._editGeneration += 1
    clearTimeout(this._timeout)
    this._updateStatus("modified")
  }

  structuralQueued() {
    if (!this._saving || !this.hasStatusTarget) return

    this.statusTarget.textContent = t("structural_queued")
  }

  removeAttachment(event) {
    event.preventDefault()
    if (this._saving) return
    const record = event.currentTarget.closest(
      "[data-erp--estimate-editor-target='item'], [data-erp--estimate-editor-target='marking']"
    )
    const hiddenContainer = record?.querySelector("[data-attachment-hidden-container]")
    const fileInput = record?.querySelector("input[type='file']")
    if (!hiddenContainer || !fileInput) return

    const signedId = event.currentTarget.dataset.signedId
    hiddenContainer.querySelectorAll("input[data-existing-attachment]").forEach(input => {
      if (input.value === signedId) input.remove()
    })
    if (!hiddenContainer.querySelector("input[data-existing-attachment]")) {
      const empty = document.createElement("input")
      empty.type = "hidden"
      empty.name = fileInput.name
      empty.value = ""
      empty.dataset.attachmentsCleared = "true"
      hiddenContainer.appendChild(empty)
    }
    event.currentTarget.closest("[data-attachment-badge]")?.remove()
    this.saveNow()
  }

  async close() {
    const url = this.hasCloseButtonTarget ? this.closeButtonTarget.dataset.closeUrl : null
    if (!url) return

    if (!this._dirty) {
      window.location.href = url
      return
    }

    const save = confirm(t("close_save_confirm"))

    if (save) {
      const success = await this._submitForm()
      if (!success) return
      window.location.href = url
      return
    }

    const closeAnyway = confirm(t("close_discard_confirm"))

    if (closeAnyway) {
      this._dirty = false
      window.location.href = url
    }
  }

  // Opens a document of the estimate (the PDF preview) in a tab of its own once pending edits are saved, the editor
  // staying in place. The tab opens before the save, while the click still counts as a user gesture: Safari blocks
  // a window.open that comes after an await. A failed save closes it, the error showing in the status.
  async openInNewTab(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.url
    const tab = window.open("", "_blank")
    if (tab) tab.opener = null

    if (!(await this.#savePending())) {
      tab?.close()
      return
    }

    if (tab) tab.location.href = url
    else window.open(url, "_blank", "noopener")
  }

  async _submitForm({ skipValidation = false } = {}) {
    if (this._saving || this._conflicted) return false

    // Empty placeholders are intentionally omitted, but any started line must satisfy the
    // browser constraints before data is sent to Rails.
    const blankItems = this._disableBlankItems()
    const invalidField = skipValidation ? null : this.element.querySelector(":invalid")
    if (invalidField) {
      this._enableItems(blankItems)
      this._dirty = true
      this._updateStatus("error", null, [this._invalidFieldMessage(invalidField)])
      this.#revealInvalidField(invalidField)
      invalidField.reportValidity()
      return false
    }

    this._dirty = false
    this._saving = true
    if (this.hasCloseButtonTarget) this.closeButtonTarget.disabled = true
    const submittedGeneration = this._editGeneration
    this._updateStatus("saving")

    // Temporarily exclude blank unsaved items from FormData
    const submittedFiles = this._fileInputSnapshot()
    const formData = new FormData(this.element)
    const submittedDestroyedFields = new Set(
      [...formData.entries()]
        .filter(([name, value]) => name.endsWith("[_destroy]") && value === "1")
        .map(([name]) => name)
    )
    this._setAttachmentControlsDisabled(true)
    let success = false
    let requestSucceeded = false

    try {
      const response = await fetch(this.element.action, {
        method: this.element.querySelector("input[name='_method']")?.value?.toUpperCase() || "POST",
        body: formData,
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      if (response.ok) {
        requestSucceeded = true
        const data = await response.json().catch(() => null)
        const superseded = this._editGeneration !== submittedGeneration
        if (data?.sections) this._syncSectionIds(data.sections)
        if (data?.lock_version != null) this._syncLockVersion(data.lock_version)
        if (data?.items) this._syncIds(data.items, "item", "items_attributes")
        if (data?.item_variants) this._syncItemVariantIds(data.item_variants)
        if (data?.item_variant_replacements && Object.keys(data.item_variant_replacements).length > 0) {
          this._dispatchVariantReplacements(data.item_variant_replacements)
        }
        if (data?.markings) this._syncIds(data.markings, "marking", "markings_attributes")
        if (data?.marking_items) this._syncMarkingItemIds(data.marking_items)
        if (data?.marking_allocations) this._syncAllocationIds(data.marking_allocations)
        if (data?.marking_extras) this._syncMarkingExtraIds(data.marking_extras)
        this._dispatchEditorEvent("autosave:records-synced")
        if (data?.editor_state) this._syncAttachmentStates(data.editor_state, submittedFiles)
        if (superseded && data?.editor_state?.customer_changed) this._invalidateCustomerFrames()
        if (!superseded && data?.editor_state) this._syncServerState(data.editor_state)
        this._purgeDestroyedRecords(submittedDestroyedFields)
        if (!superseded) this._updateStatus("saved", data?.updated_at)
        success = !superseded
      } else {
        let messages = null
        const contentType = response.headers.get("content-type") || ""
        if (contentType.includes("application/json")) {
          const data = await response.json().catch(() => null)
          messages = data?.errors
        }
        if (!messages?.length) {
          messages = [`${response.status} ${response.statusText}`]
        }
        if (response.status === 409) {
          this._conflicted = true
          this._updateStatus("conflict", null, messages)
        } else {
          this._updateStatus("error", null, messages)
          if (this.hasStatusTarget) this.statusTarget.focus()
        }
        this._dirty = true
      }
    } catch (e) {
      this._updateStatus("error", null, [e.message])
      if (this.hasStatusTarget) this.statusTarget.focus()
      this._dirty = true
    } finally {
      this._enableItems(blankItems)
      this._setAttachmentControlsDisabled(false)
      this._saving = false
      if (this.hasCloseButtonTarget) this.closeButtonTarget.disabled = false

      if (this._dirty && !["error", "conflict"].includes(this.element.dataset.saveState)) this._updateStatus("modified")
      this.dispatch("request-finished", { detail: { successful: requestSucceeded } })
      if (this._submitQueued && !this._saving && !this._conflicted && this.element.isConnected) {
        this._submitQueued = false
        this._submitForm()
      }
    }

    return success
  }

  _invalidFieldMessage(field) {
    const rawLabel = field.getAttribute("aria-label") || field.labels?.[0]?.textContent || field.name || "Champ non identifié"
    const label = rawLabel.replace(/\s+/g, " ").replace(/\s*\*\s*$/, "").trim()
    const recordLabel = this._invalidRecordLabel(field)
    const fieldLabel = recordLabel ? `${recordLabel} · ${label}` : label

    return `Champ « ${fieldLabel} » — ${this._invalidFieldReason(field)}. Corrigez cette valeur puis enregistrez à nouveau.`
  }

  _invalidFieldReason(field) {
    const validity = field.validity
    if (validity.valueMissing) return "ce champ est obligatoire"
    if (validity.typeMismatch) return "la valeur ne respecte pas le type attendu"
    if (validity.badInput) return "saisissez un nombre valide"
    if (validity.rangeUnderflow) return `la valeur doit être supérieure ou égale à ${field.min}`
    if (validity.rangeOverflow) return `la valeur doit être inférieure ou égale à ${field.max}`
    if (validity.stepMismatch) return `la valeur doit respecter un pas de ${field.step}`
    if (validity.tooShort) return `la valeur doit contenir au moins ${field.minLength} caractères`
    if (validity.tooLong) return `la valeur ne peut pas dépasser ${field.maxLength} caractères`
    if (validity.patternMismatch) return "le format saisi est invalide"

    return field.validationMessage || "la valeur saisie est invalide"
  }

  _invalidRecordLabel(field) {
    const record = field.closest("[data-record-kind]")
    if (!record) return field.closest("[data-record-label]")?.dataset.recordLabel

    const positionTarget = {
      marking: "markingPositionField",
      section: "sectionPositionField"
    }[record.dataset.recordKind]
    const positionField = positionTarget
      ? record.querySelector(`[data-erp--estimate-editor-target='${positionTarget}']`)
      : null
    const position = Math.max(0, parseInt(positionField?.value || "0", 10)) + 1

    if (record.dataset.recordKind === "textile") {
      const textiles = [...this.element.querySelectorAll("[data-record-kind='textile']")].filter(candidate => {
        const destroyField = candidate.querySelector("[data-erp--estimate-editor-target='destroyField']")
        return candidate.style.display !== "none" && destroyField?.value !== "1"
      })
      const globalPosition = Math.max(0, textiles.indexOf(record)) + 1
      const description = record.querySelector("[name$='[description]']")?.value?.trim() || "sans description"
      return `Textile T${globalPosition} « ${description} »`
    }

    if (record.dataset.recordKind === "marking") {
      const selectedTechnique = record.querySelector("input[type='radio'][name$='[marking_technique_id]']:checked")?.dataset.label
      const legacyTechnique = record.querySelector("[name$='[technique]']")?.value?.trim()
      const technique = selectedTechnique || legacyTechnique || "sans technique"
      return `Marquage M${position} « ${technique} »`
    }

    if (record.dataset.recordKind === "section") {
      const name = record.querySelector("[name$='[name]']")?.value?.trim() || "sans nom"
      return `Section S${position} « ${name} »`
    }

    return record.dataset.recordLabel
  }

  // Native constraint validation prevents the submit event itself. Capture the
  // invalid event so the persistent footer explains the exact field as well as
  // the browser tooltip.
  #handleInvalid(event) {
    const field = event.target
    if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement)) return
    if (field !== this.element.querySelector(":invalid")) return

    this._dirty = true
    this._updateStatus("error", null, [this._invalidFieldMessage(field)])
    this.#revealInvalidField(field)
    if (this.hasStatusTarget) this.statusTarget.focus()
  }

  // Disable inputs on unsaved item rows that have no description (blank new rows).
  // Returns the disabled elements so they can be re-enabled after submit.
  _disableBlankItems() {
    const disabled = []
    const items = this.element.querySelectorAll("[data-erp--estimate-editor-target='item']")

    items.forEach(item => {
      // Skip persisted items (they have an [id] hidden field with a value)
      const idField = item.querySelector("input[name*='[id]']")
      if (idField?.value) return

      // Skip items marked for destruction
      const destroyField = item.querySelector("input[name*='[_destroy]']")
      if (destroyField?.value === "1") return

      // Check if description is blank — means it's an empty placeholder row
      const desc = item.querySelector("textarea[name*='[description]']")
      if (desc && !desc.value.trim()) {
        const inputs = item.querySelectorAll("input[name], textarea[name], select[name]")
        inputs.forEach(input => {
          input.disabled = true
          disabled.push(input)
        })
      }
    })

    return disabled
  }

  _enableItems(inputs) {
    inputs.forEach(input => { input.disabled = false })
  }

  _updateStatus(status, timestamp, errors) {
    if (!this.hasStatusTarget) return

    this.statusTarget.textContent = ""
    this.statusTarget.classList.remove("text-red-300", "text-amber-200", "font-semibold")
    this.statusTarget.classList.add("text-white/70")
    this.statusTarget.setAttribute("role", ["error", "conflict"].includes(status) ? "alert" : "status")
    clearTimeout(this._hideTimeout)
    this.element.dataset.saveState = status
    this.element.querySelectorAll("[data-requires-saved]").forEach(element => {
      element.disabled = status !== "saved"
    })
    this.dispatch("state-changed", { detail: { status } })

    switch (status) {
      case "modified":
        this.statusTarget.textContent = t("modified")
        this.statusTarget.classList.remove("text-white/70")
        this.statusTarget.classList.add("text-amber-200", "font-semibold")
        break
      case "saving":
        this.statusTarget.textContent = t("saving")
        break
      case "saved":
        this.statusTarget.textContent = timestamp ? t("saved_at", { timestamp }) : t("saved")
        break
      case "error":
        if (errors?.length) {
          this.statusTarget.textContent = `${t("save_error")} : ${errors.join(" — ")}`
        } else {
          this.statusTarget.textContent = t("save_error")
        }
        this.statusTarget.classList.remove("text-white/70")
        this.statusTarget.classList.add("text-red-300", "font-semibold")
        this.statusTarget.tabIndex = -1
        break
      case "conflict": {
        const message = document.createElement("span")
        message.textContent = errors?.join(" — ") || t("conflict")
        const reload = document.createElement("button")
        reload.type = "button"
        reload.className = "ml-3 rounded border border-white/70 px-2 py-1 text-xs text-white hover:bg-white/10"
        reload.dataset.action = "erp--autosave#reloadAfterConflict"
        reload.textContent = t("reload")
        this.statusTarget.append(message, reload)
        this.statusTarget.classList.remove("text-white/70")
        this.statusTarget.classList.add("text-red-300", "font-semibold")
        break
      }
    }
  }

  reloadAfterConflict() {
    if (!confirm(t("conflict_reload_confirm"))) return

    this._dirty = false
    window.location.reload()
  }

  _syncLockVersion(lockVersion) {
    const field = this.element.querySelector("input[name='estimate[lock_version]']")
    if (field) field.value = lockVersion
  }

  // Inject server-assigned IDs into new rows so they don't get re-created on the next save.
  _syncIds(idsByPosition, targetName, attributesKey) {
    this.element.querySelectorAll(`[data-erp--estimate-editor-target='${targetName}']`).forEach(record => {
      if (record.style.display === "none") return

      const idField = record.querySelector("input[name*='[id]']")
      if (idField?.value) return

      // Target attribute rather than name matching: nested size and attachment rows carry a
      // `[position]` field too.
      const posField = record.querySelector(
        "[data-erp--estimate-editor-target='positionField'], [data-erp--estimate-editor-target='markingPositionField']"
      )
      const position = posField?.value
      if (position == null) return

      const scopedIds = targetName === "item"
        ? idsByPosition[record.closest("[data-erp--estimate-editor-target='itemsList']")?.dataset.sectionId || ""]
        : idsByPosition
      const serverId = scopedIds?.[position]
      if (!serverId) return

      const match = posField.name.match(new RegExp(`${attributesKey}\\]\\[(\\d+)\\]`))
      if (!match) return

      const hidden = document.createElement("input")
      hidden.type = "hidden"
      hidden.name = `estimate[${attributesKey}][${match[1]}][id]`
      hidden.value = serverId
      record.prepend(hidden)
      if (targetName === "item") record.dataset.itemId = serverId

      if (targetName === "marking") {
        const link = record.querySelector("[data-personalizations-link]")
        if (link?.dataset.baseUrl) {
          const url = new URL(link.dataset.baseUrl, window.location.origin)
          url.searchParams.set("marking_id", serverId)
          link.href = url.toString()
          link.classList.remove("hidden")
          record.querySelector("[data-personalizations-pending]")?.classList.add("hidden")
        }
      }
    })
  }

  // Size rows are created client-side too: without their ids the next save would insert them
  // again and trip the uniqueness index on the line.
  _syncItemVariantIds(idsByItemPosition) {
    this.element.querySelectorAll("[data-erp--estimate-editor-target='item']").forEach(item => {
      if (item.style.display === "none") return

      const itemPosition = item.querySelector("[data-erp--estimate-editor-target='positionField']")?.value
      const sectionId = item.closest("[data-erp--estimate-editor-target='itemsList']")?.dataset.sectionId || ""
      const idsByPosition = idsByItemPosition[sectionId]?.[itemPosition]
      if (!idsByPosition) return

      item.querySelectorAll("[data-size-row]").forEach(row => {
        const idField = row.querySelector("input[name*='[id]']")
        if (idField?.value) return

        const posField = row.querySelector("input[name*='[position]']")
        const serverId = idsByPosition[posField?.value]
        if (!serverId) return

        if (idField) {
          idField.value = serverId
          return
        }

        const hidden = document.createElement("input")
        hidden.type = "hidden"
        hidden.name = posField.name.replace(/\[position\]$/, "[id]")
        hidden.value = serverId
        row.prepend(hidden)
      })
    })
  }

  _syncSectionIds(sectionsMap) {
    const sections = this.element.querySelectorAll("[data-erp--estimate-editor-target='section']")
    sections.forEach(section => {
      if (section.style.display === "none") return

      const idField = section.querySelector("input[name*='[id]']")
      if (idField?.value) return

      const posField = section.querySelector("input[name*='[position]']")
      const position = posField?.value
      if (position == null) return

      const serverId = sectionsMap[position]
      if (!serverId) return

      const match = posField.name.match(/sections_attributes\]\[(\d+)\]/)
      if (!match) return
      const index = match[1]

      const hidden = document.createElement("input")
      hidden.type = "hidden"
      hidden.name = `estimate[sections_attributes][${index}][id]`
      hidden.value = serverId
      section.prepend(hidden)

      // Also update the section-id on its items list so new items get the right section_id
      const itemsList = section.querySelector("[data-erp--estimate-editor-target='itemsList']")
      if (itemsList) {
        itemsList.dataset.sectionId = serverId
        itemsList.querySelectorAll("[data-erp--estimate-editor-target='item']").forEach(item => {
          const sectionField = item.querySelector("[data-erp--estimate-editor-target='sectionIdField']")
          if (sectionField) sectionField.value = serverId
        })
      }
    })
  }

  _syncAllocationIds(idsByMarkingPosition) {
    this.element.querySelectorAll("[data-erp--estimate-editor-target='marking']").forEach(marking => {
      if (marking.style.display === "none") return

      const position = marking.querySelector("[data-erp--estimate-editor-target='markingPositionField']")?.value
      const idsByVariant = idsByMarkingPosition[position]
      if (!idsByVariant) return

      marking.querySelectorAll("[data-erp--marking-form-target='allocationRow']").forEach(row => {
        const idField = row.querySelector("input[name$='[id]']")
        if (idField?.value) return

        const variantField = row.querySelector("input[name$='[estimate_item_variant_id]']")
        const serverId = idsByVariant[variantField?.value]
        if (!variantField || !serverId) return

        if (idField) {
          idField.value = serverId
          return
        }

        const hidden = document.createElement("input")
        hidden.type = "hidden"
        hidden.name = variantField.name.replace(/\[estimate_item_variant_id\]$/, "[id]")
        hidden.value = serverId
        row.prepend(hidden)
      })
    })
  }

  _syncMarkingItemIds(idsByMarkingPosition) {
    this.element.querySelectorAll("[data-erp--estimate-editor-target='marking']").forEach(marking => {
      if (marking.style.display === "none") return

      const position = marking.querySelector("[data-erp--estimate-editor-target='markingPositionField']")?.value
      const idsByItem = idsByMarkingPosition[position]
      if (!idsByItem) return

      marking.querySelectorAll("[data-erp--marking-form-target='linkRow']").forEach(row => {
        const idField = row.querySelector("input[name$='[id]']")
        if (idField?.value) return

        const itemField = row.querySelector("input[name$='[estimate_item_id]']")
        const serverId = idsByItem[itemField?.value]
        if (!itemField || !serverId) return

        if (idField) {
          idField.value = serverId
          return
        }

        const hidden = document.createElement("input")
        hidden.type = "hidden"
        hidden.name = itemField.name.replace(/\[estimate_item_id\]$/, "[id]")
        hidden.value = serverId
        row.prepend(hidden)
      })
    })
  }

  _syncMarkingExtraIds(idsByMarkingPosition) {
    this.element.querySelectorAll("[data-erp--estimate-editor-target='marking']").forEach(marking => {
      if (marking.style.display === "none") return

      const position = marking.querySelector("[data-erp--estimate-editor-target='markingPositionField']")?.value
      const idsByExtra = idsByMarkingPosition[position]
      if (!idsByExtra) return

      marking.querySelectorAll("[data-erp--marking-form-target='extraRow']").forEach(row => {
        const idField = row.querySelector("input[name$='[id]']")
        if (idField?.value) return

        const extraField = row.querySelector("input[name$='[printing_extra_id]']")
        const serverId = idsByExtra[extraField?.value]
        if (!extraField || !serverId) return

        if (idField) {
          idField.value = serverId
          return
        }

        const hidden = document.createElement("input")
        hidden.type = "hidden"
        hidden.name = extraField.name.replace(/\[printing_extra_id\]$/, "[id]")
        hidden.value = serverId
        row.prepend(hidden)
      })
    })
  }

  _dispatchVariantReplacements(replacements) {
    this._dispatchEditorEvent("autosave:variant-replacements", { replacements })
  }

  _dispatchEditorEvent(name, detail = {}) {
    const editor = this.element.querySelector("[data-controller~='erp--estimate-editor']")
    editor?.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }))
  }

  _syncServerState(state) {
    this._syncGeneralSummary(state.general)
    if (state.customer_changed) this._invalidateCustomerFrames()
    this.element.querySelectorAll("[data-erp--estimate-editor-target='item']").forEach(item => {
      if (item.style.display === "none") return
      const sectionId = item.closest("[data-erp--estimate-editor-target='itemsList']")?.dataset.sectionId || ""
      const position = item.querySelector("[data-erp--estimate-editor-target='positionField']")?.value
      this._syncPricingRecord(item, state.items?.[sectionId]?.[position])
    })

    this.element.querySelectorAll("[data-erp--estimate-editor-target='marking']").forEach(marking => {
      if (marking.style.display === "none") return
      if (state.general?.customer_type) {
        marking.setAttribute("data-erp--marking-form-customer-type-value", state.general.customer_type)
      }
      const position = marking.querySelector("[data-erp--estimate-editor-target='markingPositionField']")?.value
      const recordState = state.markings?.[position]
      if (recordState) marking.dataset.markingExtrasCents = recordState.extras_cents || 0
      this._syncPricingRecord(marking, recordState)
    })

    this._syncTotals(state.totals)
    this.element.querySelector("[data-pricing-drift]")?.remove()
    this._syncReadiness(state.readiness)
  }

  // Page title, block 1 summary and recap rows, as saved
  _syncGeneralSummary(general) {
    if (!general) return

    const fields = {
      title: general.title, customer: general.customer, grid: general.grid,
      validity: general.validity_date, responsible: general.responsible
    }
    Object.entries(fields).forEach(([name, value]) => {
      document.querySelectorAll(`[data-estimate-field='${name}']`).forEach(element => { element.textContent = value || "" })
    })
  }

  _invalidateCustomerFrames() {
    const documents = document.getElementById("estimate-documents")
    if (documents?.getAttribute("src") && typeof documents.reload === "function") documents.reload()

    const history = document.getElementById("customer-history-content")
    if (history) {
      history.removeAttribute("src")
      history.replaceChildren()
    }
  }

  _fileInputSnapshot() {
    return new Map(Array.from(this.element.querySelectorAll("input[type='file']")).map(input => [
      input,
      this._fileInputSignature(input)
    ]))
  }

  _setAttachmentControlsDisabled(disabled) {
    this.element.querySelectorAll("input[type='file'], button[data-action*='removeAttachment']").forEach(control => {
      control.disabled = disabled
    })
  }

  _fileInputSignature(input) {
    return Array.from(input.files || [])
      .map(file => [file.name, file.size, file.lastModified].join("|"))
      .join(";")
  }

  _selectedFileError(input) {
    const acceptedTypes = input.accept.split(",").map(type => type.trim()).filter(Boolean)
    const maxFileSize = Number(input.dataset.maxFileSize)

    for (const file of input.files) {
      if (acceptedTypes.length > 0 && !acceptedTypes.includes(file.type)) {
        return I18n.t("erp.sales.js.dropzone.unsupported_type")
      }
      if (maxFileSize > 0 && file.size > maxFileSize) {
        const megabytes = maxFileSize / (1024 * 1024)
        return I18n.t("erp.sales.js.dropzone.file_too_large", { size: `${megabytes} Mo` })
      }
    }
    return null
  }

  _syncAttachmentStates(state, submittedFiles) {
    this.element.querySelectorAll("[data-erp--estimate-editor-target='item']").forEach(item => {
      if (item.style.display === "none") return
      const sectionId = item.closest("[data-erp--estimate-editor-target='itemsList']")?.dataset.sectionId || ""
      const position = item.querySelector("[data-erp--estimate-editor-target='positionField']")?.value
      this._syncAttachmentRecord(item, state.items?.[sectionId]?.[position], submittedFiles)
    })

    this.element.querySelectorAll("[data-erp--estimate-editor-target='marking']").forEach(marking => {
      if (marking.style.display === "none") return
      const position = marking.querySelector("[data-erp--estimate-editor-target='markingPositionField']")?.value
      this._syncAttachmentRecord(marking, state.markings?.[position], submittedFiles)
    })
  }

  _syncAttachmentRecord(record, state, submittedFiles) {
    if (!state || !Array.isArray(state.attachments)) return

    const fileInput = record.querySelector("input[type='file']")
    const hiddenContainer = record.querySelector("[data-attachment-hidden-container]")
    if (fileInput && hiddenContainer) {
      hiddenContainer.replaceChildren(...state.attachments.map(attachment => {
        const hidden = document.createElement("input")
        hidden.type = "hidden"
        hidden.name = fileInput.name
        hidden.value = attachment.signed_id
        hidden.dataset.existingAttachment = "true"
        return hidden
      }))
    }

    record.querySelectorAll("[data-attachment-summary]").forEach(summary => {
      summary.replaceChildren()
      if (state.attachments.length === 0) {
        const empty = document.createElement("span")
        empty.className = summary.dataset.missingClasses || "font-semibold text-erp-st-pending-fg"
        empty.dataset.artworkStatus = state.artwork_pending_from_customer ? "pending-from-customer" : "missing"
        empty.textContent = state.artwork_pending_from_customer
          ? summary.dataset.pendingFromCustomerLabel
          : summary.dataset.emptyLabel
        summary.appendChild(empty)
        return
      }

      const received = document.createElement("span")
      received.className = "font-semibold text-erp-ink-2"
      received.dataset.artworkStatus = "received"
      received.textContent = state.attachments.map(attachment => attachment.filename).join(", ")
      summary.appendChild(received)
    })

    record.querySelectorAll("[data-attachment-list]").forEach(list => {
      list.replaceChildren(...state.attachments.map(attachment => {
        const badge = document.createElement("span")
        badge.className = "inline-flex min-w-0 max-w-full items-center gap-2 rounded-full border border-erp-line-strong bg-erp-surface px-3 py-1 text-xs font-semibold"
        badge.dataset.attachmentBadge = "true"
        const filename = document.createElement("span")
        filename.textContent = attachment.filename
        const remove = document.createElement("button")
        remove.type = "button"
        remove.className = "shrink-0 cursor-pointer text-erp-danger"
        remove.dataset.action = "erp--autosave#removeAttachment"
        remove.dataset.signedId = attachment.signed_id
        remove.setAttribute("aria-label", t("remove_file", { filename: attachment.filename }))
        remove.textContent = "×"
        badge.append(filename, remove)
        return badge
      }))
    })

    const submittedSignature = submittedFiles.get(fileInput)
    if (fileInput && submittedSignature && this._fileInputSignature(fileInput) === submittedSignature) {
      fileInput.value = ""
    }
  }

  _syncPricingRecord(record, state) {
    if (!state) return

    delete record.dataset.pricingStale
    const unavailable = state.pricing_unavailable === true
    record.dataset.pricingUnavailable = String(unavailable)
    if (record.hasAttribute("data-erp--marking-form-structured-pricing-value")) {
      record.dataset.markingTotalCents = state.total_cents || 0
    }
    record.querySelector("[data-pricing-stale]")?.classList.add("hidden")
    record.querySelector("[data-pricing-pending]")?.classList.toggle("hidden", state.pricing_status !== "pending")
    record.querySelector("[data-pricing-breakdown]")?.classList.remove("opacity-50")

    const quantity = record.querySelector("[data-line-quantity]")
    const unitPrice = record.querySelector("input[name*='[unit_price]']")
    if (quantity) quantity.value = state.quantity
    if (unitPrice) {
      unitPrice.value = unavailable ? "" : (state.unit_price_cents / 100).toFixed(2)
      if (record.hasAttribute("data-item-id")) unitPrice.required = !unavailable
    }
    const marginRate = record.querySelector("input[name*='[textile_margin_rate]']")
    if (marginRate && !marginRate.value && state.margin_rate != null) {
      marginRate.dataset.inheritedMarginRate = state.margin_rate
      marginRate.placeholder = state.margin_rate
    }
    const textilePrice = record.querySelector("input[name*='[textile_price]']")
    if (textilePrice) {
      textilePrice.value = unavailable || state.textile_price_cents == null ? "" : (state.textile_price_cents / 100).toFixed(2)
    }

    record.querySelectorAll("[data-pricing-status-badge]").forEach(badge => this._syncPricingBadge(badge, state))
    record.querySelectorAll("[data-customer-price-badge]").forEach(badge => this._syncCustomerPriceBadge(record, badge, state))

    record.querySelectorAll("[data-pricing-blocker]").forEach(blocker => {
      const title = blocker.querySelector("[data-pricing-blocker-title]")
      const list = blocker.querySelector("[data-pricing-blocker-list]")
      if (title && state.pricing_blocked_title) title.textContent = state.pricing_blocked_title
      list?.replaceChildren(...state.pricing_blocking_messages.map(message => {
        const item = document.createElement("li")
        item.textContent = message
        return item
      }))
      blocker.classList.toggle("hidden", state.pricing_blocking_messages.length === 0)
    })

    const currency = formatCents
    const unitSummary = record.querySelector("[data-erp--estimate-editor-target='itemSummaryUnitPrice'], [data-erp--marking-form-target='unitPriceSummary']")
    const totalSummary = record.querySelector("[data-erp--estimate-editor-target='itemSummaryTotal'], [data-erp--marking-form-target='totalSummary']")
    const quantitySummary = record.querySelector("[data-erp--marking-form-target='quantitySummary']")
    const unavailableLabel = I18n.t("erp.sales.js.estimate_editor.pricing_unavailable")
    if (unitSummary) unitSummary.textContent = !unavailable && state.unit_price_cents > 0 ? currency(state.unit_price_cents) : "—"
    if (totalSummary) totalSummary.textContent = unavailable ? unavailableLabel : (state.total_cents > 0 ? currency(state.total_cents) : "—")
    if (quantitySummary) quantitySummary.textContent = state.quantity
    const unitDisplay = record.querySelector("[data-erp--marking-form-target='unitPriceDisplay']")
    if (unitDisplay) unitDisplay.textContent = unavailable ? I18n.t("erp.sales.js.marking_form.recalculate_required") : currency(state.unit_price_cents)
    this._syncMarkingPricingBreakdown(record, state.pricing_breakdown, unavailable)
  }

  // Textile line at the price agreed with the customer: "Tarif client" badge, and erp--estimate-item-pricing
  // leaves its price alone
  _syncCustomerPriceBadge(record, badge, state) {
    const customerPrice = state.customer_price === true
    record.dataset.customerPrice = String(customerPrice)
    badge.classList.toggle("hidden", !customerPrice)
  }

  _syncPricingBadge(badge, state) {
    badge.textContent = state.pricing_status_label
    // Textile and marking cards bring their ERP palette (ERP::EstimateTextilesHelper::ERP_PRICING_STATUS_CLASSES)
    if (!badge.dataset.statusClasses) return

    const palette = JSON.parse(badge.dataset.statusClasses)
    Object.values(palette).forEach(classes => badge.classList.remove(...classes.split(" ")))
    badge.classList.add(...(palette[state.pricing_status] || palette.pending).split(" "))
  }

  // The name of the fee's ERP extra; a fee saved before the name was kept shows its code
  _feeLabel(fee) {
    return fee.name || I18n.t("erp.sales.estimates.form.erp_fee", { code: fee.code })
  }

  _syncMarkingPricingBreakdown(record, breakdown, unavailable = false) {
    const container = record.querySelector("[data-pricing-breakdown]")
    if (!container || !breakdown) return

    const subgroups = Array.isArray(breakdown.subgroups) ? breakdown.subgroups : []
    const fees = Array.isArray(breakdown.fees) ? breakdown.fees : []
    container.classList.toggle("hidden", subgroups.length === 0 && fees.length === 0)

    const tier = container.querySelector("[data-pricing-tier]")
    if (tier) {
      tier.classList.toggle("hidden", !breakdown.bracket)
      let value = tier.querySelector("[data-pricing-tier-value]")
      if (breakdown.bracket && !value) {
        value = document.createElement("span")
        value.dataset.pricingTierValue = "true"
        tier.append(" ", value)
      }
      if (value && breakdown.bracket) {
        value.textContent = `${breakdown.bracket.quantity_min}–${breakdown.bracket.quantity_max || "∞"}`
      }
    }

    const lines = container.querySelector("[data-pricing-lines]")
    if (lines) {
      lines.replaceChildren()
      subgroups.forEach(subgroup => {
        const row = document.createElement("div")
        row.className = "grid grid-cols-[1fr_auto_auto] items-center gap-5 px-4 py-3 text-sm"
        const label = document.createElement("span")
        label.textContent = `${I18n.t(`erp.sales.estimates.form.pricing_subgroups.${subgroup.name}`, { defaultValue: subgroup.name })} · ${subgroup.quantity}`
        const unit = document.createElement("span")
        unit.className = "tabular-nums text-erp-muted"
        unit.textContent = `${this._currency(subgroup.unit_price_cents)} / pce`
        const total = document.createElement("span")
        total.className = "font-bold tabular-nums"
        total.textContent = this._currency(subgroup.subtotal_cents)
        row.append(label, unit, total)
        lines.appendChild(row)
      })
      fees.forEach(fee => {
        const row = document.createElement("div")
        row.className = "grid grid-cols-[1fr_auto] items-center gap-5 px-4 py-3 text-sm"
        const label = document.createElement("span")
        label.textContent = this._feeLabel(fee)
        const total = document.createElement("span")
        total.className = "font-bold tabular-nums"
        total.textContent = this._currency(fee.total_cents)
        row.append(label, total)
        lines.appendChild(row)
      })
    }

    const total = container.querySelector("[data-pricing-total]")
    if (total) total.textContent = unavailable ? I18n.t("erp.sales.js.estimate_editor.pricing_unavailable") : this._currency(breakdown.total_cents || 0)
  }

  _currency(cents) {
    return formatCents(cents)
  }

  _syncTotals(totals) {
    if (!totals) return
    const currency = formatCents
    const unavailable = I18n.t("erp.sales.js.estimate_editor.pricing_unavailable")
    const set = (target, value) => {
      const element = this.element.querySelector(`[data-erp--estimate-editor-target='${target}']`)
      if (element) element.textContent = value
    }
    set("totalQty", I18n.t("erp.sales.js.estimate_editor.pieces", { count: totals.textiles_quantity }))
    set("totalTextiles", totals.textiles_pricing_unavailable ? unavailable : currency(totals.textiles_subtotal_cents))
    set("totalItemsSubtotal", totals.pricing_unavailable ? unavailable : currency(totals.items_subtotal_cents))
    set("totalGlobalDiscount", `- ${currency(totals.discount_cents)}`)
    set("totalSubtotal", totals.pricing_unavailable ? unavailable : currency(totals.subtotal_cents))
    set("totalVat", totals.pricing_unavailable ? unavailable : currency(totals.vat_amount_cents))
    set("totalTtc", totals.pricing_unavailable ? unavailable : currency(totals.total_cents))

    const discountRow = this.element.querySelector("[data-erp--estimate-editor-target='globalDiscountRow']")
    discountRow?.classList.toggle("hidden", totals.pricing_unavailable || totals.discount_cents <= 0)

    const breakdown = this.element.querySelector("[data-markings-breakdown-rows]")
    if (breakdown) {
      breakdown.replaceChildren()
      Array(totals.markings_breakdown).flat().forEach(entry => {
        const row = document.createElement("div")
        row.className = "flex justify-between gap-3"
        const label = document.createElement("span")
        label.textContent = `${entry.label || "Marquage"} · ${I18n.t("erp.sales.js.estimate_editor.pieces", { count: entry.quantity })}`
        const amount = document.createElement("span")
        amount.className = "font-semibold whitespace-nowrap tabular-nums"
        amount.textContent = entry.unavailable ? unavailable : currency(entry.total_cents)
        row.append(label, amount)
        breakdown.appendChild(row)
      })
      if (Array(totals.markings_breakdown).flat().length === 0) {
        const empty = document.createElement("p")
        empty.className = "text-[#B9B4A7]"
        empty.textContent = I18n.t("erp.sales.estimates.form.no_markings")
        breakdown.appendChild(empty)
      }
    }
  }

  // The checklist comes rendered by the server; an open list of reasons stays open
  _syncReadiness(readiness) {
    const checklist = this.element.querySelector("[data-send-checklist]")
    if (!readiness?.html || !checklist) return

    const reasonsOpen = checklist.querySelector("[data-readiness-details]")?.open
    checklist.outerHTML = readiness.html
    const details = this.element.querySelector("[data-send-checklist] [data-readiness-details]")
    if (details && reasonsOpen) details.open = true
  }

  // Server has processed _destroy=1 records — drop them from the DOM so
  // subsequent autosaves don't resend stale ids that no longer exist.
  _purgeDestroyedRecords(submittedDestroyedFields = new Set()) {
    const selectors = [
      "[data-erp--estimate-editor-target='item']",
      "[data-erp--estimate-editor-target='marking']",
      "[data-erp--estimate-editor-target='section']",
      "[data-size-row]",
      "[data-erp--marking-form-target='allocationRow']",
      "[data-erp--marking-form-target='extraRow']"
    ]
    this.element.querySelectorAll(selectors.join(",")).forEach(record => {
      const destroyField = record.querySelector("input[name*='[_destroy]']")
      if (destroyField?.value !== "1" || !submittedDestroyedFields.has(destroyField.name)) return

      const reusableChoice = record.matches(
        "[data-erp--marking-form-target='allocationRow'], [data-erp--marking-form-target='extraRow']"
      )
      if (reusableChoice) {
        const idField = record.querySelector("input[name$='[id]']")
        if (idField) idField.value = ""
      } else {
        record.remove()
      }
    })
  }

  // ── Private ──────────────────────────────────────────────────

  #handleSubmit(event) {
    if (event.target !== this.element) return

    event.preventDefault()
    if (this._saving) {
      this._dirty = true
      this._editGeneration += 1
      this._submitQueued = true
      return
    }

    this._dirty = true
    clearTimeout(this._timeout)
    this._submitForm()
  }

  #revealInvalidField(field) {
    // A folded block 1 unfolds through its "Modifier" button
    if (field.closest("[data-erp--estimate-general-target='fields']")?.parentElement?.hidden) {
      this.element.querySelector("[data-erp--estimate-general-target='edit']")?.click()
    }

    // A field of a marking wizard reopens the wizard on its step
    const wizard = field.closest("[data-erp--marking-wizard-target='dialog']")
    if (wizard && !wizard.open) {
      const marking = wizard.closest("[data-erp--estimate-editor-target='marking']")
      this.application.getControllerForElementAndIdentifier(marking, "erp--marking-wizard")?.openOn(field)
    }

    const markingEditor = field.closest("[data-erp--estimate-editor-target='markingForm']")
    if (markingEditor?.classList.contains("hidden")) {
      markingEditor.closest("[data-erp--estimate-editor-target='marking']")
        ?.querySelector("[data-marking-toggle]")?.click()
    }

    let disclosure = field.closest("details")
    while (disclosure) {
      disclosure.open = true
      disclosure = disclosure.parentElement?.closest("details")
    }

    field.scrollIntoView({ behavior: "smooth", block: "center" })
    field.focus({ preventScroll: true })
  }

  #handleBeforeUnload(event) {
    if (this._dirty || this._saving) {
      event.preventDefault()
      event.returnValue = ""
    }
  }

  #handleTurboBeforeVisit(event) {
    if (!this._dirty && !this._saving) return
    if (window.confirm(t("leave_confirm"))) {
      this._dirty = false
      return
    }

    event.preventDefault()
  }

  // Waits for a save in flight (and the one it may have queued), then saves what is still pending
  async #savePending() {
    while (this._saving) {
      await new Promise(resolve => this.element.addEventListener("erp--autosave:request-finished", resolve, { once: true }))
    }
    if (this._conflicted) return false
    if (!this._dirty) return true

    return this._submitForm()
  }
}
